import { useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Col, Container, Dropdown, FloatingLabel, Form, Modal, OverlayTrigger, Row, Spinner, Table, Tooltip } from "react-bootstrap"
import { useLocation, useNavigate } from "react-router-dom"
import Notification from "../components/Notification"
import StorageLabelsPrintTrigger from "../components/StorageLabelsPrintTrigger"
import StorageProtocolPrintTrigger from "../components/StorageProtocolPrintTrigger"
import useTableData from "../hooks/useTableData"
import { useAutoRefresh } from "../hooks/useAutoRefresh"
import { createUschovaKarta, deleteUschova, fetchUschovaNapoveda, fetchUschovaNastaveni, fetchUschovy, importUschovaSkladovePozice } from "../services/api/uschovy"
import pneuLetoIcon from "../assets/images/icons/pneu-leto.png"
import pneuZimaIcon from "../assets/images/icons/pneu-zima.png"
import pneuCelorokIcon from "../assets/images/icons/pneu-celorok.png"
import diskBezIcon from "../assets/images/icons/disk-bez.png"
import diskOcelIcon from "../assets/images/icons/disk-ocel.png"
import diskHlinikIcon from "../assets/images/icons/disk-hlinik.png"

const toTrim = (v) => String(v ?? "").trim()
const toNum = (v, fallback = 0) => {
  if (v === null || v === undefined || v === "") return fallback
  const n = Number(String(v).replace(",", ".").trim())
  return Number.isFinite(n) ? n : fallback
}
const normalizeSpz = (v) => toTrim(v).toUpperCase().replace(/\s+/g, "")
const normalizeScannerInput = (value) => {
  const digitMap = { "+": "1", "ě": "2", "š": "3", "č": "4", "ř": "5", "ž": "6", "ý": "7", "á": "8", "í": "9", "é": "0" }
  return String(value || "")
    .toLocaleLowerCase("cs-CZ")
    .replace(/[+ěščřžýáíé]/g, (character) => digitMap[character] || character)
}
const toDateInput = (value) => {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}
const toDateDisplay = (value) => {
  const iso = toDateInput(value)
  if (!iso) return ""
  const [y, m, d] = iso.split("-")
  return `${d}.${m}.${y}`
}

const seasonLabelByValue = (value) => {
  const v = Number(value)
  if (v === 1) return "letni"
  if (v === 2) return "zimni"
  if (v === 3) return "celorocni"
  return ""
}
const diskLabelByValue = (value) => {
  const v = Number(value)
  if (v === 0) return "bez disku"
  if (v === 1) return "ocelovy"
  if (v === 2) return "hlinikovy"
  return ""
}

const getBoolIcon = (value) => (Number(value) === 1 ? "bi-check-circle" : "bi-x-circle")
const getBoolIconClass = (value) => (Number(value) === 1 ? "text-success" : "text-danger")
const seasonIcons = { 1: pneuLetoIcon, 2: pneuZimaIcon, 3: pneuCelorokIcon }
const diskIcons = { 0: diskBezIcon, 1: diskOcelIcon, 2: diskHlinikIcon }

const FILTER_STORAGE_KEY = "storages_active_filters_v1"
const COLUMN_STORAGE_KEY = "storages_visible_columns_v1"
const STORAGE_SCOPES = new Set(["stored", "all"])
const QUICK_REPORTS = new Set(["", "uncollected", "without-position", "low-depth"])
const STORAGE_COLUMNS = [
  { id: "datum", label: "Datum", icon: "bi-calendar3" },
  { id: "cislo", label: "Číslo", icon: "bi-hash" },
  { id: "pozice", label: "Pozice", icon: "bi-geo-alt" },
  { id: "zakaznik", label: "Zákazník", icon: "bi-person" },
  { id: "spz", label: "SPZ", icon: "bi-car-front" },
  { id: "vozidlo", label: "Vozidlo", icon: "bi-car-front-fill" },
  { id: "rozmer", label: "Rozměr", icon: "bi-arrows-angle-expand" },
  { id: "znacka", label: "Značka", icon: "bi-tag" },
  { id: "model", label: "Model", icon: "bi-card-text" },
  { id: "sezona", label: "Sezóna", icon: "bi-sun" },
  { id: "disk", label: "Disk", icon: "bi-disc" },
  { id: "pocet", label: "V úschově", icon: "bi-stack" },
  { id: "hloubka", label: "Hloubka", icon: "bi-rulers" },
  { id: "tmps", label: "TMPS", icon: "bi-broadcast-pin" },
  { id: "poklice", label: "Poklice", icon: "bi-circle" },
  { id: "srouby", label: "Šrouby", icon: "bi-gear" },
]
const DEFAULT_STORAGE_COLUMNS = STORAGE_COLUMNS.map((column) => column.id)

const loadStoredColumns = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(COLUMN_STORAGE_KEY) || "null")
    if (!Array.isArray(parsed)) return DEFAULT_STORAGE_COLUMNS
    const allowed = new Set(DEFAULT_STORAGE_COLUMNS)
    const migrated = parsed.includes("vybava")
      ? [...parsed.filter((id) => id !== "vybava"), "tmps", "poklice", "srouby"]
      : parsed
    const valid = migrated.filter((id) => allowed.has(id))
    return valid.length ? valid : DEFAULT_STORAGE_COLUMNS
  } catch {
    return DEFAULT_STORAGE_COLUMNS
  }
}

const loadStoredFilters = () => {
  const defaults = {
    filtr: {},
    dateInput: "",
    cisloInput: "",
    zakaznikInput: "",
    spzInput: "",
    poziceInput: "",
    vozidloInput: "",
    rozmerInput: "",
    znackaInput: "",
    modelInput: "",
    pocetInput: "",
    storageScope: "stored",
    quickReport: "",
  }
  try {
    if (typeof window === "undefined") return defaults
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY)
    if (!raw) return defaults
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return defaults
    if (parsed.filtr && typeof parsed.filtr === "object") {
      return {
        filtr: parsed.filtr,
        dateInput: String(parsed.dateInput ?? ""),
        cisloInput: String(parsed.cisloInput ?? ""),
        zakaznikInput: String(parsed.zakaznikInput ?? ""),
        spzInput: String(parsed.spzInput ?? ""),
        poziceInput: String(parsed.poziceInput ?? ""),
        vozidloInput: String(parsed.vozidloInput ?? ""),
        rozmerInput: String(parsed.rozmerInput ?? ""),
        znackaInput: String(parsed.znackaInput ?? ""),
        modelInput: String(parsed.modelInput ?? ""),
        pocetInput: String(parsed.pocetInput ?? ""),
        storageScope: STORAGE_SCOPES.has(parsed.storageScope) ? parsed.storageScope : "stored",
        quickReport: QUICK_REPORTS.has(parsed.quickReport) ? parsed.quickReport : "",
      }
    }
    return { ...defaults, filtr: parsed }
  } catch {
    return defaults
  }
}

const hasAnyFilter = (obj) => !!obj && Object.keys(obj).length > 0
const resolveIntakeDate = (row) => row?.datum_prijmu ?? row?.DATUM_PRIJMU ?? row?.datumPrijmu ?? row?.datum ?? row?.DATUM ?? null

const Storages = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const storedFilterState = loadStoredFilters()
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [isFiltrOpen, setIsFiltrOpen] = useState(false)
  const [filtr, setFiltr] = useState(() => {
    const { uskladneno, ...rest } = storedFilterState.filtr || {}
    return rest
  })
  const [storageScope, setStorageScope] = useState(storedFilterState.storageScope)
  const [dateInput, setDateInput] = useState(storedFilterState.dateInput || toTrim(storedFilterState.filtr.datum))
  const [cisloInput, setCisloInput] = useState(storedFilterState.cisloInput || toTrim(storedFilterState.filtr.cislo))
  const [zakaznikInput, setZakaznikInput] = useState(storedFilterState.zakaznikInput || toTrim(storedFilterState.filtr.zakaznik))
  const [spzInput, setSpzInput] = useState(storedFilterState.spzInput || toTrim(storedFilterState.filtr.spz))
  const [poziceInput, setPoziceInput] = useState(storedFilterState.poziceInput || toTrim(storedFilterState.filtr.pozice))
  const [vozidloInput, setVozidloInput] = useState(storedFilterState.vozidloInput || toTrim(storedFilterState.filtr.vozidlo_znacka))
  const [rozmerInput, setRozmerInput] = useState(storedFilterState.rozmerInput || toTrim(storedFilterState.filtr.rozmer))
  const [znackaInput, setZnackaInput] = useState(storedFilterState.znackaInput || toTrim(storedFilterState.filtr.pneu_znacka))
  const [modelInput, setModelInput] = useState(storedFilterState.modelInput || toTrim(storedFilterState.filtr.pneu_model))
  const [pocetInput, setPocetInput] = useState(storedFilterState.pocetInput || "")
  const [isDateInvalid, setIsDateInvalid] = useState(false)
  const [isPocetInvalid, setIsPocetInvalid] = useState(false)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [showCreateCardModal, setShowCreateCardModal] = useState(false)
  const [validatedCreateCard, setValidatedCreateCard] = useState(false)
  const [isSavingCreateCard, setIsSavingCreateCard] = useState(false)
  const [createCardForm, setCreateCardForm] = useState({ spz: "", jmeno: "", firma: "" })
  const [spzLookup, setSpzLookup] = useState([])
  const [jmenoLookup, setJmenoLookup] = useState([])
  const [firmaLookup, setFirmaLookup] = useState([])
  const [selectedExistingZakazka, setSelectedExistingZakazka] = useState(0)
  const [openPrintDropdownId, setOpenPrintDropdownId] = useState(null)
  const [showColumnsModal, setShowColumnsModal] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState(loadStoredColumns)
  const [quickReport, setQuickReport] = useState(storedFilterState.quickReport)
  const [depthLimits, setDepthLimits] = useState({ limit_leto: 1.6, limit_zima: 4, limit_celorok: 4 })
  const [showPositionImportModal, setShowPositionImportModal] = useState(false)
  const positionImportRef = useRef(null)
  const [positionImportPending, setPositionImportPending] = useState(false)
  const [positionImportError, setPositionImportError] = useState("")
  const [positionImportErrors, setPositionImportErrors] = useState([])
  const [positionImportResult, setPositionImportResult] = useState("")

  const isColumnVisible = (id) => visibleColumns.includes(id)

  useEffect(() => {
    try {
      window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibleColumns))
    } catch {
      // Nastavení sloupců může fungovat i bez localStorage.
    }
  }, [visibleColumns])

  useEffect(() => {
    setFiltr((prev) => {
      if (!prev?.stredisko && !prev?.strediskoOperator) return prev
      const { stredisko, strediskoOperator, ...rest } = prev
      return rest
    })
  }, [])

  useEffect(() => {
    const notice = location.state?.notice
    if (!notice) return
    setNotifications((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: notice.type || "info",
        message: notice.message || "",
      },
    ])
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.pathname, location.state, navigate])

  useEffect(() => {
    if (!showCreateCardModal) return
    const q = normalizeSpz(createCardForm.spz)
    if (q.length < 3) {
      setSpzLookup([])
      return
    }
    let canceled = false
    const t = setTimeout(async () => {
      try {
        const rows = await fetchUschovaNapoveda("spz", q)
        if (!canceled) setSpzLookup(Array.isArray(rows) ? rows : [])
      } catch {
        if (!canceled) setSpzLookup([])
      }
    }, 250)
    return () => { canceled = true; clearTimeout(t) }
  }, [showCreateCardModal, createCardForm.spz])

  useEffect(() => {
    if (!showCreateCardModal) return
    const q = toTrim(createCardForm.jmeno)
    if (q.length < 3) {
      setJmenoLookup([])
      return
    }
    let canceled = false
    const t = setTimeout(async () => {
      try {
        const rows = await fetchUschovaNapoveda("jmeno", q)
        if (!canceled) setJmenoLookup(Array.isArray(rows) ? rows : [])
      } catch {
        if (!canceled) setJmenoLookup([])
      }
    }, 250)
    return () => { canceled = true; clearTimeout(t) }
  }, [showCreateCardModal, createCardForm.jmeno])

  useEffect(() => {
    if (!showCreateCardModal) return
    const q = toTrim(createCardForm.firma)
    if (q.length < 3) {
      setFirmaLookup([])
      return
    }
    let canceled = false
    const t = setTimeout(async () => {
      try {
        const rows = await fetchUschovaNapoveda("firma", q)
        if (!canceled) setFirmaLookup(Array.isArray(rows) ? rows : [])
      } catch {
        if (!canceled) setFirmaLookup([])
      }
    }, 250)
    return () => { canceled = true; clearTimeout(t) }
  }, [showCreateCardModal, createCardForm.firma])

  const parseDateFilter = (input) => {
    const filter = { datum1: "", datum2: "", datumOperator: "" }
    const dateRegex = /^\d{1,2}\.\d{1,2}\.\d{4}$/
    const v = toTrim(input)
    if (v.includes("..")) {
      const [d1, d2] = v.split("..").map((x) => x.trim())
      if (dateRegex.test(d1) && dateRegex.test(d2)) {
        filter.datum1 = d1; filter.datum2 = d2; filter.datumOperator = ".."
      }
    } else if (/^(<|>|<=|>=)/.test(v)) {
      const m = v.match(/^(<|>|<=|>=)(\d{1,2}\.\d{1,2}\.\d{4})$/)
      if (m && dateRegex.test(m[2])) { filter.datumOperator = m[1]; filter.datum1 = m[2] }
    } else if (dateRegex.test(v)) {
      filter.datumOperator = "="; filter.datum1 = v
    }
    setIsDateInvalid(v !== "" && !filter.datumOperator)
    return filter
  }
  const parseTextFilter = (input, key) => {
    const value = toTrim(input)
    const opKey = `${key}Operator`
    if (!value) return { [key]: "", [opKey]: "" }
    if (value.endsWith("*")) return { [key]: value.slice(0, -1), [opKey]: "startsWith" }
    return { [key]: value, [opKey]: "=" }
  }
  const parseNumericFilter = (input, key, setInvalid) => {
    const v = toTrim(input).replace(",", ".")
    const key1 = `${key}1`
    const key2 = `${key}2`
    const opKey = `${key}Operator`
    const filter = { [key1]: "", [key2]: "", [opKey]: "" }
    if (v.includes("..")) {
      const [n1, n2] = v.split("..").map((x) => Number(x))
      if (Number.isFinite(n1) && Number.isFinite(n2)) { filter[key1] = n1; filter[key2] = n2; filter[opKey] = ".." }
    } else if (/^(<=|>=|<|>)/.test(v)) {
      const m = v.match(/^(<=|>=|<|>)(\d+(\.\d+)?)$/)
      if (m) { filter[key1] = Number(m[2]); filter[opKey] = m[1] }
    } else if (/^\d+(\.\d+)?$/.test(v)) {
      filter[key1] = Number(v); filter[opKey] = "="
    }
    setInvalid(v !== "" && !filter[opKey]); return filter
  }
  const parseSelectFilter = (value, key) => {
    const v = toTrim(value)
    return { [key]: v === "" ? "" : Number(v) }
  }

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [rows, settings] = await Promise.all([
        fetchUschovy(),
        fetchUschovaNastaveni().catch(() => null),
      ])
      if (settings) {
        setDepthLimits({
          limit_leto: toNum(settings.limit_leto, 1.6),
          limit_zima: toNum(settings.limit_zima, 4),
          limit_celorok: toNum(settings.limit_celorok, 4),
        })
      }
      setItems((rows || []).map((r) => ({
        ...r,
        datum_prijmu: resolveIntakeDate(r),
        datum_vydeje: r.datum_vydeje ?? r.DATUM_VYDEJE ?? null,
        datum: resolveIntakeDate(r),
        vytvoreno: resolveIntakeDate(r),
        id_zakazky: Number(r.id_zakazky),
        cislo: toTrim(r.cislo),
        pozice: toTrim(r.pozice),
        delka_uschovy_dny: Number.isFinite(Number(r.delka_uschovy_dny)) ? Number(r.delka_uschovy_dny) : null,
        stav: Number(r.stav ?? 1),
        smer: Number(r.smer ?? 1),
        typ_vydeje: (r.typ_vydeje === null || r.typ_vydeje === undefined || r.typ_vydeje === "") ? null : Number(r.typ_vydeje),
        jmeno: toTrim(r.jmeno),
        firma: toTrim(r.firma),
        spz: toTrim(r.spz).toUpperCase(),
        vozidlo_znacka: toTrim(r.vozidlo_znacka),
        sezona: Number(r.sezona || 1),
        disk: Number(r.disk || 0),
        tmps: Number(r.tmps || 0),
        poklice: Number(r.poklice || 0),
        srouby: Number(r.srouby || 0),
        rozmer: toTrim(r.rozmer),
        pneu_znacka: toTrim(r.pneu_znacka),
        pneu_model: toTrim(r.pneu_model),
        hloubka_list: toTrim(r.hloubka_list),
        hloubka_prumer: toNum(r.hloubka_prumer, 0),
        pneu_pocet: Number(r.pneu_pocet || 0),
        pneu_v_uschove: Number(r.stav ?? 1) === 1 ? Number(r.pneu_pocet || 0) : 0,
      })))
    } catch (error) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: "error", message: error?.message || "Nepodarilo se nacist uschovy." }])
    } finally {
      setIsLoading(false)
      setHasLoaded(true)
    }
  }

  useEffect(() => { loadData() }, [])

  useAutoRefresh(loadData, {
    shouldRun: () => !showDeleteModal && !showCreateCardModal && !showPositionImportModal && !isDeleting && !isSavingCreateCard && !isLoading,
    deps: [showDeleteModal, showCreateCardModal, showPositionImportModal, isDeleting, isSavingCreateCard, isLoading],
  })
  useEffect(() => {
    try {
      if (typeof window === "undefined") return
      const hasInputs = [dateInput, cisloInput, zakaznikInput, spzInput, poziceInput, vozidloInput, rozmerInput, znackaInput, modelInput, pocetInput]
        .some((v) => String(v || "").trim() !== "")
      if (hasAnyFilter(filtr) || hasInputs || storageScope !== "stored" || quickReport) {
        window.localStorage.setItem(
          FILTER_STORAGE_KEY,
          JSON.stringify({
            filtr,
            dateInput,
            cisloInput,
            zakaznikInput,
            spzInput,
            poziceInput,
            vozidloInput,
            rozmerInput,
            znackaInput,
            modelInput,
            pocetInput,
            storageScope,
            quickReport,
          })
        )
      } else {
        window.localStorage.removeItem(FILTER_STORAGE_KEY)
      }
    } catch {
      // ignore localStorage errors
    }
  }, [filtr, dateInput, cisloInput, zakaznikInput, spzInput, poziceInput, vozidloInput, rozmerInput, znackaInput, modelInput, pocetInput, storageScope, quickReport])

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && isFiltrOpen && setIsFiltrOpen(false)
    const onOut = (e) =>
      isFiltrOpen &&
      !e.target.closest(".offcanvas-custom") &&
      !e.target.closest("#offcanvas-custom-btn") &&
      !e.target.closest(".filter-active-item") &&
      !e.target.closest(".btn-clear-filters") &&
      setIsFiltrOpen(false)
    document.addEventListener("keydown", onEsc); document.addEventListener("mousedown", onOut)
    return () => { document.removeEventListener("keydown", onEsc); document.removeEventListener("mousedown", onOut) }
  }, [isFiltrOpen])

  const hasActiveFilters = useMemo(
    () => Boolean(quickReport) || Object.keys(filtr || {}).some((k) => filtr[k] !== "" && filtr[k] !== undefined && filtr[k] !== null),
    [filtr, quickReport]
  )

  const filteredData = useMemo(() => {
    const parseDate = (str) => { const [d, m, y] = String(str || "").split(".").map(Number); return new Date(y, m - 1, d) }
    const dateToKey = (dt) => dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate()
    const evalText = (value, f, op) => !f ? true : (op === "startsWith" ? String(value || "").toUpperCase().startsWith(String(f).toUpperCase()) : String(value || "").toUpperCase().includes(String(f).toUpperCase()))
    const evalNum = (value, op, n1, n2) => {
      if (!op) return true
      const v = Number(value || 0), a = Number(n1), b = Number(n2)
      if (op === "..") return v >= a && v <= b
      if (op === "<") return v < a
      if (op === "<=") return v <= a
      if (op === ">") return v > a
      if (op === ">=") return v >= a
      if (op === "=") return v === a
      return true
    }
    return items.filter((x) => {
      const isStored = Number(x.stav ?? 1) === 1
      const depths = String(x.hloubka_list || "")
        .split("|")
        .map((value) => String(value).trim())
        .filter(Boolean)
        .map((value) => Number(value.replace(",", ".")))
        .filter(Number.isFinite)
      const depthLimit = Number(x.sezona) === 1
        ? depthLimits.limit_leto
        : Number(x.sezona) === 2
          ? depthLimits.limit_zima
          : depthLimits.limit_celorok
      const reportOk = !quickReport
        || (quickReport === "uncollected" && isStored && Number(x.delka_uschovy_dny) >= 365)
        || (quickReport === "without-position" && isStored && !toTrim(x.pozice))
        || (quickReport === "low-depth" && isStored && depths.some((depth) => depth < depthLimit))
      let dateOk = true
      if (filtr.datumOperator) {
        const itemKey = dateToKey(new Date(x.datum_prijmu || x.datum))
        const d1 = filtr.datum1 ? dateToKey(parseDate(filtr.datum1)) : null
        const d2 = filtr.datum2 ? dateToKey(parseDate(filtr.datum2)) : null
        if (filtr.datumOperator === "..") dateOk = itemKey >= d1 && itemKey <= d2
        if (filtr.datumOperator === "<") dateOk = itemKey < d1
        if (filtr.datumOperator === "<=") dateOk = itemKey <= d1
        if (filtr.datumOperator === ">") dateOk = itemKey > d1
        if (filtr.datumOperator === ">=") dateOk = itemKey >= d1
        if (filtr.datumOperator === "=") dateOk = itemKey === d1
      }
      return reportOk && dateOk
        && (storageScope === "all" || isStored)
        && evalText(x.cislo, filtr.cislo, filtr.cisloOperator)
        && evalText(x.firma || x.jmeno, filtr.zakaznik, filtr.zakaznikOperator)
        && evalText(x.spz, filtr.spz, filtr.spzOperator)
        && evalText(x.pozice, filtr.pozice, filtr.poziceOperator)
        && evalText(x.vozidlo_znacka, filtr.vozidlo_znacka, filtr.vozidlo_znackaOperator)
        && evalText(x.rozmer, filtr.rozmer, filtr.rozmerOperator)
        && evalText(x.pneu_znacka, filtr.pneu_znacka, filtr.pneu_znackaOperator)
        && evalText(x.pneu_model, filtr.pneu_model, filtr.pneu_modelOperator)
        && ((filtr.sezona === "" || filtr.sezona === undefined) ? true : Number(x.sezona) === Number(filtr.sezona))
        && ((filtr.disk === "" || filtr.disk === undefined) ? true : Number(x.disk) === Number(filtr.disk))
        && ((filtr.tmps === "" || filtr.tmps === undefined) ? true : Number(x.tmps) === Number(filtr.tmps))
        && ((filtr.poklice === "" || filtr.poklice === undefined) ? true : Number(x.poklice) === Number(filtr.poklice))
        && ((filtr.srouby === "" || filtr.srouby === undefined) ? true : Number(x.srouby) === Number(filtr.srouby))
        && evalNum(x.pneu_v_uschove, filtr.pocetOperator, filtr.pocet1, filtr.pocet2)
    })
  }, [items, filtr, quickReport, depthLimits, storageScope])

  const table = useTableData(filteredData, {
    defaultSortField: "datum",
    defaultSortDirection: "desc",
    searchableFields: ["cislo", "vytvoreno", "datum", "jmeno", "firma", "spz", "vozidlo_znacka", "pozice", "sezona", "rozmer", "pneu_znacka", "pneu_model", "pneu_v_uschove", "hloubka_prumer", "hloubka_list", "stav", "smer", "typ_vydeje"],
  })

  const clearAllFilters = () => {
    setFiltr({})
    setQuickReport("")
    setDateInput("")
    setCisloInput("")
    setZakaznikInput("")
    setSpzInput("")
    setPoziceInput("")
    setVozidloInput("")
    setRozmerInput("")
    setZnackaInput("")
    setModelInput("")
    setPocetInput("")
    setIsDateInvalid(false); setIsPocetInvalid(false)
  }

  const openPositionImport = () => {
    if (positionImportRef.current) positionImportRef.current.value = ""
    setPositionImportError("")
    setPositionImportErrors([])
    setPositionImportResult("")
    setShowPositionImportModal(true)
  }

  const handlePositionImport = async () => {
    const codes = String(positionImportRef.current?.value || "")
      .split(/\s+/)
      .map((value) => value.trim())
      .filter(Boolean)
    setPositionImportError("")
    setPositionImportErrors([])
    setPositionImportResult("")
    if (!codes.length) {
      setPositionImportError("Načtěte alespoň jeden čárový kód.")
      return
    }

    setPositionImportPending(true)
    try {
      const result = await importUschovaSkladovePozice(codes)
      setPositionImportResult(result?.message || "Pozice úschov byly aktualizovány.")
      setPositionImportErrors(Array.isArray(result?.chyby) ? result.chyby : [])
      await loadData()
    } catch (error) {
      setPositionImportError(error?.message || "Hromadné přiřazení pozic se nepodařilo.")
      setPositionImportErrors(Array.isArray(error?.data?.chyby) ? error.data.chyby : [])
    } finally {
      setPositionImportPending(false)
    }
  }

  const openCreate = () => {
    setValidatedCreateCard(false)
    setCreateCardForm({ spz: "", jmeno: "", firma: "" })
    setSpzLookup([])
    setJmenoLookup([])
    setFirmaLookup([])
    setSelectedExistingZakazka(0)
    setShowCreateCardModal(true)
  }
  const handleCreateCardChange = (e) => {
    const { name, value } = e.target
    setCreateCardForm((prev) => ({
      ...prev,
      [name]: name === 'spz' ? normalizeSpz(value) : value,
    }))
  }
  const handleCreateCardSpzBlur = () => {
    setCreateCardForm((prev) => ({ ...prev, spz: normalizeSpz(prev.spz) }))
  }
  const createCardSpzNorm = normalizeSpz(createCardForm.spz)
  const existingSpzRows = useMemo(() => {
    if (createCardSpzNorm.length < 6) return []
    const map = new Map()
    for (const x of items) {
      if (normalizeSpz(x.spz) !== createCardSpzNorm) continue
      const key = Number(x.id_vozidla || 0) || Number(x.id_zakazky || 0)
      if (!map.has(key)) map.set(key, x)
    }
    return Array.from(map.values())
  }, [items, createCardSpzNorm])
  const createCardSpzExists = existingSpzRows.length > 0
  const existingCustomerRows = useMemo(() => {
    const j = toTrim(createCardForm.jmeno).toLowerCase()
    const f = toTrim(createCardForm.firma).toLowerCase()
    const jPicked = j.length >= 3 && jmenoLookup.some((v) => toTrim(v).toLowerCase() === j)
    const fPicked = f.length >= 3 && firmaLookup.some((v) => toTrim(v).toLowerCase() === f)
    if (!jPicked && !fPicked) return []
    const map = new Map()
    for (const x of items) {
      const jx = toTrim(x.jmeno).toLowerCase()
      const fx = toTrim(x.firma).toLowerCase()
      const matchJ = !jPicked || jx === j
      const matchF = !fPicked || fx === f
      if (!matchJ || !matchF) continue
      const key = Number(x.id_vozidla || 0) || Number(x.id_zakazky || 0)
      if (!map.has(key)) map.set(key, x)
    }
    return Array.from(map.values())
  }, [items, createCardForm.jmeno, createCardForm.firma, jmenoLookup, firmaLookup])
  const foundRows = createCardSpzExists ? existingSpzRows : existingCustomerRows

  useEffect(() => {
    if (!foundRows.length) {
      setSelectedExistingZakazka(0)
      return
    }
    if (!foundRows.some((r) => Number(r.id_zakazky) === Number(selectedExistingZakazka))) {
      setSelectedExistingZakazka(createCardSpzExists ? Number(foundRows[0]?.id_zakazky || 0) : 0)
    }
  }, [foundRows, selectedExistingZakazka, createCardSpzExists])

  const handleCreateCardSubmit = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (selectedExistingZakazka) {
      setShowCreateCardModal(false)
      navigate("/storagedetail", { state: { id: Number(selectedExistingZakazka) } })
      return
    }
    const hasCustomer = !!toTrim(createCardForm.jmeno) || !!toTrim(createCardForm.firma)
    if (form && !form.checkValidity()) {
      setValidatedCreateCard(true)
      return
    }
    if (!hasCustomer) {
      setValidatedCreateCard(true)
      return
    }
    if (createCardSpzExists) {
      if (!selectedExistingZakazka) {
        setValidatedCreateCard(true)
        return
      }
      setShowCreateCardModal(false)
      navigate("/storagedetail", { state: { id: Number(selectedExistingZakazka) } })
      return
    }
    if (!createCardSpzExists && selectedExistingZakazka) {
      setShowCreateCardModal(false)
      navigate("/storagedetail", { state: { id: Number(selectedExistingZakazka) } })
      return
    }
    try {
      setIsSavingCreateCard(true)
      const payload = {
        spz: createCardSpzNorm,
        jmeno: toTrim(createCardForm.jmeno),
        firma: toTrim(createCardForm.firma),
      }
      const result = await createUschovaKarta(payload)
      setShowCreateCardModal(false)
      setNotifications((prev) => [...prev, { id: Date.now(), type: "success", message: "Karta vozidla byla vytvořena." }])
      await loadData()
      if (result?.id_anchor) navigate("/storagedetail", { state: { id: Number(result.id_anchor) } })
    } catch (error) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: "danger", message: error?.message || "Uložení se nepodařilo." }])
    } finally {
      setIsSavingCreateCard(false)
    }
  }
  const openDelete = (row) => { setSelectedItem(row); setShowDeleteModal(true) }
  const handleDelete = async (e) => { e.preventDefault(); if (!selectedItem?.id_zakazky) return; setIsDeleting(true); try { await deleteUschova(selectedItem.id_zakazky); setShowDeleteModal(false); setNotifications((prev) => [...prev, { id: Date.now(), type: "success", message: "Úschova byla odstraněna." }]); await loadData() } finally { setIsDeleting(false) } }

  const searchValue = table.searchQuery ?? table.searchTerm ?? ""
  const setSearchValue = (value) => {
    if (typeof table.setSearchQuery === "function") table.setSearchQuery(value)
    else if (typeof table.setSearchTerm === "function") table.setSearchTerm(value)
  }
  const updateFilter = (partial) => setFiltr((s) => ({ ...s, ...partial }))
  const createCardSpzOk = createCardSpzNorm.length >= 7
  const createCardUsingSelected = !!selectedExistingZakazka
  const createCardSpzVisualValid = createCardSpzOk || createCardUsingSelected
  const createCardHasCustomer = !!toTrim(createCardForm.jmeno) || !!toTrim(createCardForm.firma)
  const createCardCustomerVisualValid = createCardHasCustomer || createCardUsingSelected

  return (
    <>
      <div className="d-flex flex-wrap align-items-end gap-1 mb-3">
        <div>
          <span className="d-block small text-muted mb-1">Rozsah přehledu</span>
          <div className="btn-group" role="group" aria-label="Rozsah zobrazených úschov">
            <Button type="button" variant={storageScope === "stored" ? "primary" : "outline-primary"} onClick={() => setStorageScope("stored")}>
              <i className="bi bi-box-seam me-2"></i>Jen uskladněné
            </Button>
            <Button type="button" variant={storageScope === "all" ? "primary" : "outline-primary"} onClick={() => setStorageScope("all")}>
              <i className="bi bi-list-ul me-2"></i>Všechny zakázky
            </Button>
          </div>
        </div>
        <Button type="button" className="ms-3" variant="outline-primary" title="Vybrat sloupce přehledu" onClick={() => setShowColumnsModal(true)}>
          <i className="bi bi-layout-three-columns me-2"></i>Sloupce
        </Button>
        <Button type="button" variant="outline-primary" title="Hromadně načíst pozice úschov čárovým skenerem" onClick={openPositionImport}>
          <i className="bi bi-upc-scan me-2"></i>Import
        </Button>
      </div>
      {hasActiveFilters && (
        <h6>Aktuální filtr</h6>
      )}
      <div className="filter-active-wrapper">
        {(filtr.datum1 || filtr.datum2 || filtr.datum) && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Datum</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { datum, datum1, datum2, datumOperator, ...r } = p; return r }); setDateInput(""); setIsDateInvalid(false) }}>
              {dateInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.cislo && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Číslo</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { cislo, cisloOperator, ...r } = p; return r }); setCisloInput("") }}>
              {cisloInput || filtr.cislo} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.zakaznik && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Zákazník</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { zakaznik, zakaznikOperator, ...r } = p; return r }); setZakaznikInput("") }}>
              {zakaznikInput || filtr.zakaznik} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.spz && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>SPZ</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { spz, spzOperator, ...r } = p; return r }); setSpzInput("") }}>
              {spzInput || filtr.spz} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.rozmer && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Rozměr</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { rozmer, rozmerOperator, ...r } = p; return r }); setRozmerInput("") }}>
              {rozmerInput || filtr.rozmer} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.pneu_znacka && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Značka</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { pneu_znacka, pneu_znackaOperator, ...r } = p; return r }); setZnackaInput("") }}>
              {znackaInput || filtr.pneu_znacka} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.pneu_model && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Model</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { pneu_model, pneu_modelOperator, ...r } = p; return r }); setModelInput("") }}>
              {modelInput || filtr.pneu_model} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.pocet1 || filtr.pocet2) && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Počet</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { pocet1, pocet2, pocetOperator, ...r } = p; return r }); setPocetInput(""); setIsPocetInvalid(false) }}>
              {pocetInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.sezona !== undefined && filtr.sezona !== "") && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Sezona</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setFiltr((p) => { const { sezona, ...r } = p; return r })}>
              {seasonLabelByValue(filtr.sezona)} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.disk !== undefined && filtr.disk !== "") && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Disk</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setFiltr((p) => { const { disk, ...r } = p; return r })}>
              {diskLabelByValue(filtr.disk)} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.tmps !== undefined && filtr.tmps !== "") && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>TMPS</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setFiltr((p) => { const { tmps, ...r } = p; return r })}>
              {Number(filtr.tmps) === 1 ? "ano" : "ne"} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.poklice !== undefined && filtr.poklice !== "") && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Poklice</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setFiltr((p) => { const { poklice, ...r } = p; return r })}>
              {Number(filtr.poklice) === 1 ? "ano" : "ne"} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.srouby !== undefined && filtr.srouby !== "") && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Šrouby</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setFiltr((p) => { const { srouby, ...r } = p; return r })}>
              {Number(filtr.srouby) === 1 ? "ano" : "ne"} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.pozice && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Pozice</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { pozice, poziceOperator, ...r } = p; return r }); setPoziceInput("") }}>
              {poziceInput || filtr.pozice} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.vozidlo_znacka && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Vozidlo</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { vozidlo_znacka, vozidlo_znackaOperator, ...r } = p; return r }); setVozidloInput("") }}>
              {vozidloInput || filtr.vozidlo_znacka} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {quickReport && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Rychlý report</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setQuickReport("")}>
              {quickReport === "uncollected" ? "nevyzvednuté 365+ dní" : quickReport === "without-position" ? "bez pozice" : "dezén pod limitem"}
              <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {hasActiveFilters && (
          <button className="btn-clear-filters" onClick={clearAllFilters}>
            <i className="bi bi-trash"></i>
          </button>
        )}
      </div>

      <div className="row d-flex align-items-center justify-content-space my-2">
        <div className="col">
          <button className="btn btn-primary me-1" onClick={openCreate}>
            <i className="bi bi-plus-circle me-2"></i>Přidat
          </button>
          <button id="offcanvas-custom-btn" className="btn btn-outline-primary me-1" onClick={() => setIsFiltrOpen((s) => !s)}>
            <i className="bi bi-sliders me-2"></i>Filtr
          </button>
          <Dropdown className="d-inline me-1">
            <Dropdown.Toggle variant="outline-primary" id="storages-quick-filters" title="Rychlé reporty úschov">
              <i className="bi bi-bar-chart me-2"></i>Reporty
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item active={quickReport === "uncollected"} onClick={() => setQuickReport("uncollected")}>
                <i className="bi bi-clock-history me-2"></i>Nevyzvednuté (365+ dní)
              </Dropdown.Item>
              <Dropdown.Item active={quickReport === "without-position"} onClick={() => setQuickReport("without-position")}>
                <i className="bi bi-geo-alt me-2"></i>Úschovy bez pozice
              </Dropdown.Item>
              <Dropdown.Item active={quickReport === "low-depth"} onClick={() => setQuickReport("low-depth")}>
                <i className="bi bi-exclamation-triangle me-2"></i>Dezény pod limitem
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="col">
          <div className="search-wrapper">
            <span className="search-icon"><i className="bi bi-search"></i></span>
            <input
              type="text"
              placeholder="Hledat..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {searchValue && (
              <span className="clear-icon" role="button" onClick={() => setSearchValue("")}>
                <i className="bi bi-x"></i>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={`offcanvas-custom ${isFiltrOpen ? "open" : ""}`}>
        <div className="d-flex justify-content-between align-items-center p-4 mb-3">
          <h4 className="my-1">Filtrování úschov</h4>
          <div>
            <button type="button" className="btn" title="Zrušit vše" onClick={clearAllFilters}><i className="bi bi-trash"></i></button>
            <button type="button" className="btn" title="Zavřít" onClick={() => setIsFiltrOpen(false)}><i className="bi bi-x-lg"></i></button>
          </div>
        </div>
        <div className="offcanvas-custom-body" style={{ maxHeight: "calc(100vh - 140px)", overflowY: "auto" }}>
          <Container className="px-4">
            <Row className="g-1">
              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="datum">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, operátory &lt; &gt; &lt;= &gt;=, rozsah přes ..</Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={`${(filtr.datum1 || filtr.datum2 || filtr.datumOperator || filtr.datum) ? "border-primary border-opacity-25" : ""} ${isDateInvalid ? "is-invalid" : ""}`.trim()}
                      value={dateInput}
                      onChange={(e) => setDateInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseDateFilter(e.target.value))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseDateFilter(e.currentTarget.value))}
                    />
                    <Form.Control.Feedback type="invalid">neplatný formát datumu</Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="číslo">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.cislo || filtr.cisloOperator) ? "border-primary border-opacity-25" : ""}
                      value={cisloInput}
                      onChange={(e) => setCisloInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "cislo"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "cislo"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>


              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="zákazník">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.zakaznik || filtr.zakaznikOperator) ? "border-primary border-opacity-25" : ""}
                      value={zakaznikInput}
                      onChange={(e) => setZakaznikInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "zakaznik"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "zakaznik"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="spz">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.spz || filtr.spzOperator) ? "border-primary border-opacity-25" : ""}
                      value={spzInput}
                      onChange={(e) => setSpzInput(e.target.value.toUpperCase())}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "spz"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "spz"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="pozice">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.pozice || filtr.poziceOperator) ? "border-primary border-opacity-25" : ""}
                      value={poziceInput}
                      onChange={(e) => setPoziceInput(e.target.value.toUpperCase())}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "pozice"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "pozice"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="vozidlo">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.vozidlo_znacka || filtr.vozidlo_znackaOperator) ? "border-primary border-opacity-25" : ""}
                      value={vozidloInput}
                      onChange={(e) => setVozidloInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "vozidlo_znacka"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "vozidlo_znacka"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="rozměr">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.rozmer || filtr.rozmerOperator) ? "border-primary border-opacity-25" : ""}
                      value={rozmerInput}
                      onChange={(e) => setRozmerInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "rozmer"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "rozmer"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="značka">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.pneu_znacka || filtr.pneu_znackaOperator) ? "border-primary border-opacity-25" : ""}
                      value={znackaInput}
                      onChange={(e) => setZnackaInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "pneu_znacka"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "pneu_znacka"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="model">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, lze použít * </Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.pneu_model || filtr.pneu_modelOperator) ? "border-primary border-opacity-25" : ""}
                      value={modelInput}
                      onChange={(e) => setModelInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseTextFilter(e.target.value, "pneu_model"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseTextFilter(e.currentTarget.value, "pneu_model"))}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="počet">
                    <OverlayTrigger placement="top" overlay={<Tooltip>potvrzení Enter, operátory &lt; &gt; &lt;= &gt;=, rozsah přes ..</Tooltip>}>
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={`${filtr.pocetOperator ? "border-primary border-opacity-25" : ""} ${isPocetInvalid ? "is-invalid" : ""}`.trim()}
                      value={pocetInput}
                      onChange={(e) => setPocetInput(e.target.value)}
                      onBlur={(e) => updateFilter(parseNumericFilter(e.target.value, "pocet", setIsPocetInvalid))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseNumericFilter(e.currentTarget.value, "pocet", setIsPocetInvalid))}
                    />
                    <Form.Control.Feedback type="invalid">neplatný číselný formát</Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="sezóna">
                    <Form.Select
                      className={(filtr.sezona !== undefined && filtr.sezona !== "") ? "border-primary border-opacity-25" : ""}
                      value={filtr.sezona ?? ""}
                      onChange={(e) => updateFilter(parseSelectFilter(e.target.value, "sezona"))}
                      onBlur={(e) => updateFilter(parseSelectFilter(e.target.value, "sezona"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseSelectFilter(e.currentTarget.value, "sezona"))}
                    >
                      <option value="">vše</option>
                      <option value="1">letní</option>
                      <option value="2">zimní</option>
                      <option value="3">celoroční</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="disk">
                    <Form.Select
                      className={(filtr.disk !== undefined && filtr.disk !== "") ? "border-primary border-opacity-25" : ""}
                      value={filtr.disk ?? ""}
                      onChange={(e) => updateFilter(parseSelectFilter(e.target.value, "disk"))}
                      onBlur={(e) => updateFilter(parseSelectFilter(e.target.value, "disk"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseSelectFilter(e.currentTarget.value, "disk"))}
                    >
                      <option value="">vše</option>
                      <option value="0">bez disku</option>
                      <option value="1">ocelový</option>
                      <option value="2">hliníkový</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="tmps">
                    <Form.Select
                      className={(filtr.tmps !== undefined && filtr.tmps !== "") ? "border-primary border-opacity-25" : ""}
                      value={filtr.tmps ?? ""}
                      onChange={(e) => updateFilter(parseSelectFilter(e.target.value, "tmps"))}
                      onBlur={(e) => updateFilter(parseSelectFilter(e.target.value, "tmps"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseSelectFilter(e.currentTarget.value, "tmps"))}
                    >
                      <option value="">vše</option>
                      <option value="1">ano</option>
                      <option value="0">ne</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="poklice">
                    <Form.Select
                      className={(filtr.poklice !== undefined && filtr.poklice !== "") ? "border-primary border-opacity-25" : ""}
                      value={filtr.poklice ?? ""}
                      onChange={(e) => updateFilter(parseSelectFilter(e.target.value, "poklice"))}
                      onBlur={(e) => updateFilter(parseSelectFilter(e.target.value, "poklice"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseSelectFilter(e.currentTarget.value, "poklice"))}
                    >
                      <option value="">vše</option>
                      <option value="1">ano</option>
                      <option value="0">ne</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <FloatingLabel label="šrouby">
                    <Form.Select
                      className={(filtr.srouby !== undefined && filtr.srouby !== "") ? "border-primary border-opacity-25" : ""}
                      value={filtr.srouby ?? ""}
                      onChange={(e) => updateFilter(parseSelectFilter(e.target.value, "srouby"))}
                      onBlur={(e) => updateFilter(parseSelectFilter(e.target.value, "srouby"))}
                      onKeyDown={(e) => e.key === "Enter" && updateFilter(parseSelectFilter(e.currentTarget.value, "srouby"))}
                    >
                      <option value="">vše</option>
                      <option value="1">ano</option>
                      <option value="0">ne</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      <div className="row my-2"><div className="col">
        {isLoading && <div className="d-flex align-items-center text-muted small mb-2"><Spinner animation="border" size="sm" className="me-2" />Načítám úschovy...</div>}
        <Table responsive hover striped className="storage-overview-table my-3"><thead><tr>
  {isColumnVisible("datum") && <th onClick={() => table.handleSort("datum")}>datum <i className={`bi ${table.sortField === "datum" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("cislo") && <th onClick={() => table.handleSort("cislo")}>číslo <i className={`bi ${table.sortField === "cislo" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("pozice") && <th onClick={() => table.handleSort("pozice")}>pozice <i className={`bi ${table.sortField === "pozice" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("zakaznik") && <th onClick={() => table.handleSort("jmeno")}>zákazník <i className={`bi ${table.sortField === "jmeno" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("spz") && <th onClick={() => table.handleSort("spz")}>SPZ <i className={`bi ${table.sortField === "spz" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("vozidlo") && <th onClick={() => table.handleSort("vozidlo_znacka")}>vozidlo <i className={`bi ${table.sortField === "vozidlo_znacka" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("rozmer") && <th onClick={() => table.handleSort("rozmer")}>rozměr <i className={`bi ${table.sortField === "rozmer" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("znacka") && <th onClick={() => table.handleSort("pneu_znacka")}>značka <i className={`bi ${table.sortField === "pneu_znacka" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("model") && <th onClick={() => table.handleSort("pneu_model")}>model <i className={`bi ${table.sortField === "pneu_model" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("sezona") && <th onClick={() => table.handleSort("sezona")}>sezóna <i className={`bi ${table.sortField === "sezona" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("disk") && <th onClick={() => table.handleSort("disk")}>disk <i className={`bi ${table.sortField === "disk" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("pocet") && <th onClick={() => table.handleSort("pneu_v_uschove")}>v úschově <i className={`bi ${table.sortField === "pneu_v_uschove" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("hloubka") && <th className="storages-col-depth" onClick={() => table.handleSort("hloubka_list")}>hloubka <i className={`bi ${table.sortField === "hloubka_list" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("tmps") && <th onClick={() => table.handleSort("tmps")}>tmps <i className={`bi ${table.sortField === "tmps" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("poklice") && <th onClick={() => table.handleSort("poklice")}>poklice <i className={`bi ${table.sortField === "poklice" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  {isColumnVisible("srouby") && <th onClick={() => table.handleSort("srouby")}>šrouby <i className={`bi ${table.sortField === "srouby" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i></th>}
  <th className="text-end">akce</th>
</tr></thead><tbody>
  {table.data.length > 0 ? table.data.map((row) => <tr key={row.id_zakazky} onDoubleClick={() => navigate("/storagedetail", { state: { id: row.id_zakazky } })} style={{ cursor: "pointer" }}>
    {isColumnVisible("datum") && <td>{toDateDisplay(row.datum_prijmu || row.datum) || "-"}</td>}
    {isColumnVisible("cislo") && <td>{row.cislo || "-"}</td>}
    {isColumnVisible("pozice") && <td>{row.pozice || "-"}</td>}
    {isColumnVisible("zakaznik") && <td>{row.firma || row.jmeno || "-"}</td>}
    {isColumnVisible("spz") && <td>{row.spz || "-"}</td>}
    {isColumnVisible("vozidlo") && <td>{row.vozidlo_znacka || "-"}</td>}
  {isColumnVisible("rozmer") && <td>{row.rozmer || "-"}</td>}
    {isColumnVisible("znacka") && <td>{row.pneu_znacka || "-"}</td>}
    {isColumnVisible("model") && <td>{row.pneu_model || "-"}</td>}
    {isColumnVisible("sezona") && <td><img src={seasonIcons[row.sezona]} alt={seasonLabelByValue(row.sezona) || "sezóna"} title={seasonLabelByValue(row.sezona) || "Sezóna"} width="24" height="24" /></td>}
    {isColumnVisible("disk") && <td><img src={diskIcons[row.disk]} alt={diskLabelByValue(row.disk) || "disk"} title={diskLabelByValue(row.disk) || "Disk"} width="24" height="24" /></td>}
    {isColumnVisible("pocet") && <td>{row.pneu_v_uschove || 0}</td>}
    {isColumnVisible("hloubka") && <td className="storages-col-depth">{row.hloubka_list || "-"}</td>}
    {isColumnVisible("tmps") && <td><i title="TMPS" className={`bi ${getBoolIcon(row.tmps)} ${getBoolIconClass(row.tmps)}`}></i></td>}
    {isColumnVisible("poklice") && <td><i title="Poklice" className={`bi ${getBoolIcon(row.poklice)} ${getBoolIconClass(row.poklice)}`}></i></td>}
    {isColumnVisible("srouby") && <td><i title="Šrouby" className={`bi ${getBoolIcon(row.srouby)} ${getBoolIconClass(row.srouby)}`}></i></td>}
    <td className="text-end">
      <div className="d-inline-flex align-items-center gap-1 flex-nowrap">
      <Dropdown
        align="end"
        show={openPrintDropdownId === row.id_zakazky}
        onToggle={(nextShow) => setOpenPrintDropdownId(nextShow ? row.id_zakazky : null)}
        onClick={(e) => e.stopPropagation()}
        className="d-inline-block"
      >
        <Dropdown.Toggle className="btn-table btn-table-no-caret" title="Tiskové sestavy">
          <i className="bi bi-printer"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="px-3 py-2 rounded-2"
          style={{ minWidth: 160 }}
          renderOnMount
          popperConfig={{
            strategy: "fixed",
            modifiers: [
              {
                name: "computeStyles",
                options: {
                  adaptive: false,
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["top-end", "top-start", "bottom-end"],
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport",
                  padding: 8,
                },
              },
            ],
          }}
        >
          <div className="px-2 pt-1 pb-2 text-center text-muted">Tiskové sestavy</div>
          <Dropdown.Divider className="my-1" />
          <StorageProtocolPrintTrigger
            zakazkaId={Number(row.id_zakazky)}
            renderButton={({ onClick, disabled }) => (
              <Dropdown.Item
                as="button"
                type="button"
                onClick={() => {
                  setOpenPrintDropdownId(null)
                  onClick()
                }}
                disabled={disabled}
                className="px-0 py-2 d-flex align-items-center gap-2"
              >
                <i className="bi bi-file-earmark-text"></i>
                <span className="small text-muted">Protokol</span>
              </Dropdown.Item>
            )}
          />
          <StorageLabelsPrintTrigger
            zakazkaId={Number(row.id_zakazky)}
            renderButton={({ onClick, disabled }) => (
              <Dropdown.Item
                as="button"
                type="button"
                onClick={() => {
                  setOpenPrintDropdownId(null)
                  onClick()
                }}
                disabled={disabled}
                className="px-0 py-2 d-flex align-items-center gap-2"
              >
                <i className="bi bi-bookmark"></i>
                <span className="small text-muted">Štítky 90x65mm</span>
              </Dropdown.Item>
            )}
          />
        </Dropdown.Menu>
      </Dropdown>
      <button 
        className="btn-table" 
        title="Detail - karta vozidla"
        onClick={() => navigate("/storagedetail", { state: { id: row.id_zakazky } })}
        >
          <i className="bi bi-pencil-square"></i>
        </button>
      <button 
        className="btn-table" 
        title="Odstranit zakázku"
        onClick={() => openDelete(row)}
      >
        <i className="bi bi-trash"></i>
      </button>
      </div>
    </td>
  </tr>) : hasLoaded ? <tr className="no-data-row"><td colSpan={visibleColumns.length + 1} className="border-0"><div className="d-block my-3 text-center"><i className="bi bi-journal-text fs-4"></i><p className="small">Nebyly nalezeny žádné údaje.</p></div></td></tr> : null}
</tbody></Table>
        <div className="row d-flex align-items-center justify-content-between mt-2">
          <div className="col-auto d-flex flex-wrap gap-3 small">
            <span className="d-inline-flex align-items-center gap-1"><img src={pneuLetoIcon} alt="" width="24" height="24" /> léto</span>
            <span className="d-inline-flex align-items-center gap-1"><img src={pneuZimaIcon} alt="" width="24" height="24" /> zima</span>
            <span className="d-inline-flex align-items-center gap-1"><img src={pneuCelorokIcon} alt="" width="24" height="24" /> celorok</span>
            <span className="d-inline-flex align-items-center gap-1"><img src={diskBezIcon} alt="" width="24" height="24" /> bez disku</span>
            <span className="d-inline-flex align-items-center gap-1"><img src={diskOcelIcon} alt="" width="24" height="24" /> ocel</span>
            <span className="d-inline-flex align-items-center gap-1"><img src={diskHlinikIcon} alt="" width="24" height="24" /> hliník</span>
            <span className="d-inline-flex align-items-center gap-1"><i className="bi bi-check-circle text-success"></i> ano</span>
            <span className="d-inline-flex align-items-center gap-1"><i className="bi bi-x-circle text-danger"></i> ne</span>
          </div>
          <div className="col pagination small text-end">
            {table.totalItems > 0 && (
              <>
                <span>{table.startIndex + 1} – {Math.min(table.endIndex, table.totalItems)} z {table.totalItems}</span>
                <button className="btn btn-sm border-0 p-0" onClick={table.handlePrevPage} disabled={table.currentPage === 1}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="btn btn-sm border-0 p-0" onClick={table.handleNextPage} disabled={table.currentPage === table.totalPages}>
                  <i className="bi bi-chevron-right"></i>
                </button>
                <select value={table.itemsPerPage} onChange={(e) => table.setItemsPerPage(Number(e.target.value))}>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </>
            )}
          </div>
        </div>
      </div></div>

      <Modal show={showPositionImportModal} onHide={() => !positionImportPending && setShowPositionImportModal(false)} keyboard centered scrollable size="lg">
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-2">
          <Modal.Title className="text-primary lh-1">Import pozic<br />
            <small className="text-muted fs-6">hromadné načtení čárových kódů</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <p className="small text-muted mb-3">
            Nejprve načtěte kód skladové pozice a za něj všechny sady uložené na této pozici. Od každé sady stačí naskenovat jednu pneumatiku. Poté pokračujte další pozicí a jejími sadami. Každý kód ponechte na samostatném řádku.
          </p>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="small text-muted">Načtené kódy</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={14}
                  ref={positionImportRef}
                  className="font-monospace"
                  disabled={positionImportPending}
                  onInput={(event) => {
                    const normalized = normalizeScannerInput(event.currentTarget.value)
                    if (normalized !== event.currentTarget.value) event.currentTarget.value = normalized
                  }}
                  placeholder={'9500000025368\n9861221003826\n9861221004700...'}
                />
              </Form.Group>
              <div className="small text-muted mt-2">
                Znaky české číselné řady se během skenování automaticky převádějí na číslice.
              </div>
            </Col>
            <Col xs={12} md={6} className="px-md-4 px-xl-5">
              <div className="h-100">
                <div className="fw-semibold mb-3"><i className="bi bi-upc-scan me-2"></i>Přenos dat:</div>
                <div className="row g-0 border border-secondary rounded overflow-hidden small bg-body">
                  <div className="col-6 p-2 border-end border-bottom border-secondary">
                    <div className="d-flex justify-content-between fw-semibold mb-1">
                      <span>Main Menu</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>1. Scanner</div>
                    <div aria-hidden="true">&nbsp;</div>
                  </div>
                  <div className="col-6 p-2 border-bottom border-secondary">
                    <div className="d-flex justify-content-between fw-semibold mb-1">
                      <span>Scanner</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>2. Upload</div>
                    <div aria-hidden="true">&nbsp;</div>
                  </div>
                  <div className="col-6 p-2 border-end border-secondary">
                    <div className="d-flex justify-content-between fw-semibold mb-1">
                      <span>Upload</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>1. Unsent</div>
                  </div>
                  <div className="col-6 p-2">
                    <div className="d-flex justify-content-between">
                      <span>Sent: ...</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>Total: ...</div>
                    <div className="d-flex justify-content-between fw-semibold mt-1"><span>OK</span><span>Back</span></div>
                  </div>
                </div>
                <p className="small text-primary mt-3 mb-0">
                  <i className="bi bi-info-circle me-2"></i>Po nahrání doporučujeme skener vymazat!
                </p>
                <div className="fw-semibold mt-4 mb-3"><i className="bi bi-trash me-2"></i>Smazání dat:</div>
                <div className="row g-0 border border-secondary rounded overflow-hidden small bg-body">
                  <div className="col-6 p-2 border-end border-bottom border-secondary">
                    <div className="d-flex justify-content-between fw-semibold mb-1">
                      <span>Main Menu</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>1. Scanner</div>
                    <div aria-hidden="true">&nbsp;</div>
                  </div>
                  <div className="col-6 p-2 border-bottom border-secondary">
                    <div className="d-flex justify-content-between fw-semibold mb-1">
                      <span>Scanner</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>3. Delete</div>
                    <div aria-hidden="true">&nbsp;</div>
                  </div>
                  <div className="col-6 p-2 border-end border-secondary">
                    <div className="d-flex justify-content-between fw-semibold mb-1">
                      <span>Attention!</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>Press OK to continue!</div>
                    <div className="d-flex justify-content-between fw-semibold mt-1"><span>OK</span><span>Back</span></div>
                  </div>
                  <div className="col-6 p-2">
                    <div className="d-flex justify-content-between fw-semibold mb-1">
                      <span>Attention!</span><span className="text-muted"><i className="bi bi-reception-4 me-1"></i><i className="bi bi-battery-half"></i></span>
                    </div>
                    <div>Done!</div>
                    <div className="d-flex justify-content-between fw-semibold mt-1"><span>OK</span><span>Back</span></div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          {positionImportError ? (
            <Alert variant="danger" className="mt-3 mb-0">
              <div>{positionImportError}</div>
              {positionImportErrors.length ? (
                <ul className="mb-0 mt-2 ps-3">
                  {positionImportErrors.map((error, index) => <li key={`${index}-${error}`}>{error}</li>)}
                </ul>
              ) : null}
            </Alert>
          ) : null}
          {positionImportResult ? <Alert variant="success" className="mt-3 mb-0">{positionImportResult}</Alert> : null}
          {!positionImportError && positionImportErrors.length ? (
            <Alert variant="warning" className="mt-3 mb-0">
              <div>Následující řádky nebyly zpracovány:</div>
              <ul className="mb-0 mt-2 ps-3">
                {positionImportErrors.map((error, index) => <li key={`${index}-${error}`}>{error}</li>)}
              </ul>
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-start px-4 pb-4">
          <div>
            <Button className="me-1" variant="secondary" onClick={() => setShowPositionImportModal(false)} disabled={positionImportPending}>
              <i className="bi bi-arrow-left me-2"></i>Zpět
            </Button>
            <Button variant="primary" onClick={handlePositionImport} disabled={positionImportPending}>
              {positionImportPending ? <Spinner size="sm" className="me-2" /> : <i className="bi bi-play-fill me-2"></i>}
              {positionImportPending ? "Zpracovávám..." : "Zpracovat"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showColumnsModal} onHide={() => setShowColumnsModal(false)} keyboard centered>
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-2">
          <Modal.Title className="text-primary lh-1">Vzhled přehledu<br />
            <small className="text-muted fs-6">vyberte zobrazené sloupce</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Row className="g-2">
            {STORAGE_COLUMNS.map((column) => {
              const selected = isColumnVisible(column.id)
              return (
                <Col xs={6} md={4} key={column.id}>
                  <button
                    type="button"
                    className={`btn w-100 h-100 text-start p-3 border ${selected ? "btn-primary" : "btn-light"}`}
                    aria-pressed={selected}
                    onClick={() => setVisibleColumns((current) => {
                      if (current.includes(column.id)) {
                        return current.length > 1 ? current.filter((id) => id !== column.id) : current
                      }
                      return [...current, column.id]
                    })}
                  >
                    <span className="d-flex align-items-center justify-content-between gap-2">
                      <span><i className={`bi ${column.icon} me-2`}></i>{column.label}</span>
                      <i className={`bi ${selected ? "bi-check-circle-fill" : "bi-circle"}`}></i>
                    </span>
                  </button>
                </Col>
              )
            })}
          </Row>
          <div className="small text-muted mt-3">Nastavení se automaticky uloží pro tento prohlížeč.</div>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-start px-4 pb-4">
          <Button variant="secondary" onClick={() => setShowColumnsModal(false)}>
            <i className="bi bi-arrow-left me-2"></i>Zpět
          </Button>
          <Button variant="outline-primary" onClick={() => setVisibleColumns(DEFAULT_STORAGE_COLUMNS)}>
            <i className="bi bi-arrow-counterclockwise me-2"></i>Výchozí vzhled
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateCardModal} fullscreen="xl-down" onHide={() => setShowCreateCardModal(false)} keyboard={true} centered size="xl">
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">Úschova<br />
            <small className="text-muted fs-6">přidat nové vozidlo a zákazníka</small>
          </Modal.Title>
        </Modal.Header>
        <Form noValidate onSubmit={handleCreateCardSubmit}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-1">
              <Row className="g-2 align-items-start">
                <Col lg={4}>
                  <Row className="g-0">
                    <Col xs={10}>
                      <FloatingLabel label="spz">
                        <Form.Control
                          required
                          name="spz"
                          value={createCardForm.spz}
                          onChange={handleCreateCardChange}
                          onBlur={handleCreateCardSpzBlur}
                          minLength={7}
                          maxLength={12}
                          title="SPZ musí mít minimálně 7 znaků."
                          list="create-card-spz-list"
                          isValid={validatedCreateCard && createCardSpzVisualValid}
                          isInvalid={validatedCreateCard && !createCardSpzVisualValid}
                        />
                      </FloatingLabel>
                      <datalist id="create-card-spz-list">
                        {spzLookup.map((x) => <option key={`spz-${x}`} value={x} />)}
                      </datalist>
                    </Col>
                    <Col xs={2}>
                      <Button
                        type="button"
                        disabled
                        className="w-100 h-100 rounded-start-0 border-0"
                        variant={createCardUsingSelected ? "success" : (createCardSpzExists ? "danger" : (createCardSpzOk ? "success" : "secondary"))}
                        title={createCardUsingSelected ? "Vybrána existující karta vozidla." : (createCardSpzExists ? "Tuto SPZ již v systému evidujeme." : (createCardSpzOk ? "SPZ je validní" : "SPZ musí mít minimálně 7 znaků"))}
                      >
                        <i className={`bi ${createCardUsingSelected ? "bi-check-circle" : (createCardSpzExists ? "bi-x-circle" : (createCardSpzOk ? "bi-check-circle" : "bi-dash-circle"))}`}></i>
                      </Button>
                    </Col>
                  </Row>
                </Col>
                <Col lg={4}>
                  <FloatingLabel label="jméno">
                    <Form.Control
                      name="jmeno"
                      value={createCardForm.jmeno}
                      onChange={handleCreateCardChange}
                      list="create-card-jmeno-list"
                      isValid={validatedCreateCard && createCardCustomerVisualValid}
                      isInvalid={validatedCreateCard && !createCardCustomerVisualValid}
                    />
                  </FloatingLabel>
                  <datalist id="create-card-jmeno-list">
                    {jmenoLookup.map((x) => <option key={`jmeno-${x}`} value={x} />)}
                  </datalist>
                </Col>
                <Col lg={4}>
                  <FloatingLabel label="firma">
                    <Form.Control
                      name="firma"
                      value={createCardForm.firma}
                      onChange={handleCreateCardChange}
                      list="create-card-firma-list"
                      isValid={validatedCreateCard && createCardCustomerVisualValid}
                      isInvalid={validatedCreateCard && !createCardCustomerVisualValid}
                    />
                  </FloatingLabel>
                  <datalist id="create-card-firma-list">
                    {firmaLookup.map((x) => <option key={`firma-${x}`} value={x} />)}
                  </datalist>
                </Col>
              </Row>
              {foundRows.length > 0 && (
                <div className="mt-4">
                  <div className="text-muted mb-2">Nalezena shoda s existujícími záznamy</div>
                  <Table responsive size="sm" className="mb-0 small">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>spz</th>
                        <th>jméno zákazníka</th>
                        <th>název firmy</th>
                        <th>adresa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {foundRows.map((row, idx) => (
                        <tr key={`existing-spz-${row.id_zakazky}-${idx}`}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={Number(selectedExistingZakazka) === Number(row.id_zakazky)}
                              onChange={() =>
                                setSelectedExistingZakazka((prev) =>
                                  Number(prev) === Number(row.id_zakazky) ? 0 : Number(row.id_zakazky)
                                )
                              }
                            />
                          </td>
                          <td>{row.spz || "-"}</td>
                          <td>{row.jmeno || "-"}</td>
                          <td>{row.firma || "-"}</td>
                          <td>{`${row.ulice || ""} ${row.psc || ""} ${row.obec || ""}`.trim() || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Container>
          </Modal.Body>
          <Modal.Footer className="border-0 d-flex justify-content-between px-3 pb-3">
            <div>
              <Button variant="primary" type="submit" disabled={isSavingCreateCard}>
                {isSavingCreateCard ? "Ukládám..." : ((createCardSpzExists || selectedExistingZakazka) ? "Přejít na kartu vozidla" : "Přidat")}
              </Button>
              <Button variant="secondary" className="ms-1" onClick={() => setShowCreateCardModal(false)}>
                Zavřít
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {showDeleteModal && selectedItem && 
        <Modal show={showDeleteModal} fullscreen="xl-down" onHide={() => setShowDeleteModal(false)} keyboard={true} centered>
          <Modal.Header closeButton className="border-0 px-4 pt-4">
            <Modal.Title className="text-primary lh-1">Úschova<br/>
              <small className="text-muted fs-6">odstranit záznam</small>
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleDelete}>
            <Modal.Body className="px-4">
              <Alert variant="warning" className="d-flex align-items-center mt-2 mb-0">
                <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                <span>
                Chcete skutečně odstranit zakázku číslo <strong>{selectedItem.cislo}</strong>? Tato operace je nevratná.
                </span>
              </Alert>
            </Modal.Body>
            <Modal.Footer className="border-0 d-flex justify-content-start px-4 pb-3">
              <Button variant="primary mx-0" type="submit" disabled={isDeleting}>{isDeleting ? "Mazání..." : "Smazat"}</Button>
              <Button variant="secondary ms-1" onClick={() => setShowDeleteModal(false)}>Zavřít</Button>
            </Modal.Footer>
          </Form>
        </Modal>
      }

      <Notification notifications={notifications} setNotifications={setNotifications} />
    </>
  )
}

export default Storages










